import { styled } from '@/stitches.config';
import {
  type ChangeEvent,
  type CSSProperties,
  type PointerEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useVideoStore } from '../hooks/use-video-store';
import { Duration } from './duration';

type PlayerTime = { seconds: number; percent: number; duration: number };

export function Scrubber() {
  const { player, setTime } = useVideoStore((state) => ({
    player: state.player,
    setTime: state.setTime,
  }));

  const [percent, setPercent] = useState<number>(0);

  const timeUpdateHandler = useCallback(
    ({ percent }: PlayerTime) => setPercent(percent),
    []
  );

  const handlePointerDown = useCallback(() => {
    // Stop updating the percentage
    player.off('timeupdate', timeUpdateHandler);
  }, [player, timeUpdateHandler]);

  const handlePointerUp = useCallback(
    (event: PointerEvent<HTMLInputElement>) => {
      const percent = +event.currentTarget.value;

      setTime(percent);
      setPercent(percent);

      // Resume updating the percentage
      player.on('timeupdate', timeUpdateHandler);
    },
    [player, timeUpdateHandler, setPercent, setTime]
  );

  useEffect(() => {
    player.on('timeupdate', timeUpdateHandler);

    return () => player.off('timeupdate', timeUpdateHandler);
  }, [player, timeUpdateHandler]);

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setPercent(parseFloat(event.currentTarget.value));
  }

  return (
    <ScrubberContainer>
      <Range
        style={{ '--progress': percent } as CSSProperties}
        type="range"
        min={0}
        max={0.999999}
        step="any"
        value={percent}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onChange={handleOnChange}
      />
      <Duration percent={percent} />
    </ScrubberContainer>
  );
}

const ScrubberContainer = styled('div', {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
});

const rangeThumbStyles = {
  position: 'relative',
  zIndex: 1,
  appearance: 'none',
  '-webkit-appearance': 'none',
  background: '$foreground',
  border: '0 !important',
  borderRadius: '50%',
  cursor: 'pointer',
  height: '$2',
  width: '$2',
  flexGrow: 1,
  transition: 'scale 0.1s ease-in-out',
};

const Range = styled('input', {
  cursor: 'pointer',
  appearance: 'none',
  '-webkit-appearance': 'none',
  backgroundColor: '$gray4',
  border: 0,
  height: '2px',
  position: 'relative',
  width: '100%',
  minWidth: '20px', // Make sure the peg will always fit to avoid it jumping around while expanding
  marginLeft: '$3',

  '&:hover': {
    '&::before': {
      backgroundColor: '$gray11',
    },
    '&::-webkit-slider-thumb': {
      backgroundColor: '$gray11',
      scale: '1.2',
    },
    '&::-moz-slider-thumb': {
      backgroundColor: '$gray12',
      scale: '1.2',
    },
    '&::-ms-thumb': {
      backgroundColor: '$gray11',
      scale: '1.2',
    },
  },

  '&::before': {
    backgroundColor: '$foreground',
    content: '',
    height: '100%',
    position: 'absolute',
    width: `calc((var(--progress) / 0.999999) * 100%)`,
  },
  '&::-webkit-slider-thumb': rangeThumbStyles,
  '&::-moz-slider-thumb': rangeThumbStyles,
  '&::-ms-thumb': rangeThumbStyles,
});
